import * as React from 'react'
import { forwardRef } from 'react'
import { cva } from 'class-variance-authority'
import type { VariantProps } from 'class-variance-authority'
import { SimpleTooltipProps, SimpleTooltip } from '../tooltip'
import { Slot } from '@radix-ui/react-slot'

export const $iconButton = cva(
  'rounded text-center disabled:cursor-not-allowed disabled:opacity-60 leading-none inline-flex justify-center items-center cursor-default font-medium focus-visible:outline outline-dark-control-base outline-offset-1 p-1 enabled:hover:bg-dark-shade-hover',
  {
    variants: {
      intent: {
        primary:
          'bg-dark-control-base enabled:hover:!bg-dark-control-highlight text-white disabled:!opacity-30',
        secondary:
          'bg-dark-shade border border-dark-border-solid text-dark-bright enabled:hover:bg-dark-shade-hover',
      },
      size: {
        xs: 'size-6',
        petite: 'size-7',
        sm: 'size-8',
        md: 'size-10',
      },
    },
    defaultVariants: {},
  }
)

export type IconButtonProps = React.JSX.IntrinsicElements['button'] &
  VariantProps<typeof $iconButton> & { tooltip?: SimpleTooltipProps } & {
    asChild?: boolean
  }

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ children, className, tooltip, intent, size, asChild, ...rest }, ref) => {
    const TooltipWrapper = React.useMemo(
      () =>
        tooltip
          ? (props: { children?: React.ReactNode }) => (
              <SimpleTooltip {...tooltip}>{props.children}</SimpleTooltip>
            )
          : (props: { children?: React.ReactNode }) => <>{props.children}</>,
      [tooltip]
    )
    const Comp = asChild ? Slot : 'button'
    return (
      <TooltipWrapper>
        <Comp
          className={$iconButton({
            class: className,
            intent,
            size,
          })}
          {...rest}
          ref={ref}
        >
          {children}
        </Comp>
      </TooltipWrapper>
    )
  }
)

IconButton.displayName = 'IconButton'
